import { useMemo } from "react";
import { useInfiniteQuery, useQuery } from "react-query";
import { SortingState } from "@tanstack/react-table";
import { useApi, apiResolver } from "../../../api/useApi";
import {
  ExploreInteractionType,
  ExploreInteractionRow,
  ExploreAggInteractionRow,
  ExploreView,
  CreateExploreView,
} from "./Explore.model";
import { Filter } from "./Explore.filter";
import {
  ExploreAggColumnType,
  ExploreColumnType,
} from "./columns/ExploreColum.model";
import {
  AllowedAggOrderBy,
  AllowedInteractionOrderBy,
} from "@/v2/features/explore/explore-display-options/ExploreDisplayOptions.model";

export function buildParams({
  source,
  sorting,
  filter,
  groupBy,
  orderBy,
  orderDir,
  column,
}: {
  source?: ExploreInteractionType;
  sorting?: SortingState;
  filter?: Filter;
  groupBy?: string;
  orderBy?: AllowedInteractionOrderBy | AllowedAggOrderBy;
  orderDir?: "asc" | "desc";
  column?: ExploreColumnType;
}) {
  const params = new URLSearchParams();

  if (source) {
    params.append("sources[]", source);
  }

  if (orderBy) params.append("orderBy", orderBy);
  if (orderDir) params.append("orderDir", orderDir);

  if (filter) {
    const filterString = JSON.stringify(filter);
    params.append("filter", filterString);
  }

  if (groupBy) {
    params.append("groupBy", groupBy);
  }

  if (column) {
    params.append("column", column);
  }

  return params.toString();
}

type ExploreInteractionsResponse = {
  scanTime: number;
  results: ExploreInteractionRow[];
  next?: string | false;
};

type ExploreInteractionsCountResponse = {
  scanTime: number;
  count: number;
};

type ExploreDistinctValues = (string | number | { name: string } | null)[];

type ExploreInteractionsValuesResponse = Record<string, ExploreDistinctValues>;

export type ExploreAggInteractionsResponse = {
  scanTime: number;
  results: ExploreAggInteractionRow[];
};

export type ExploreInteractionValueDistributionResponse = {
  scanTime: number;
  values: ExploreDistinctValues;
  results: Record<string, number | string | null>[];
};

export type ExploreViewsResponse = ExploreView[];

export const useExploreApi = () => {
  const { api } = useApi();

  return useMemo(
    () => ({
      getInteractions: (params: string) =>
        apiResolver<ExploreInteractionsResponse>(
          api.get(`/platform/v2/explore/interactions${params}`)
        ),
      getInteractionsCount: (params: string) =>
        apiResolver<ExploreInteractionsCountResponse>(
          api.get(`/platform/v2/explore/interactions/count${params}`)
        ),
      getInteractionsValues: (params: string) =>
        apiResolver<ExploreInteractionsValuesResponse>(
          api.get(`/platform/v2/explore/interactions/values${params}`)
        ),
      getAggregatedInteractions: (params: string) =>
        apiResolver<ExploreAggInteractionsResponse>(
          api.get(`/platform/v2/explore/aggregated-interactions${params}`)
        ),
      getInteractionValueDistribution: (params: string) =>
        apiResolver<ExploreInteractionValueDistributionResponse>(
          api.get(
            `/platform/v2/explore/interactions/value-distribution${params}`
          )
        ),
      getViews: () =>
        apiResolver<ExploreViewsResponse>(
          api.get(`/platform/v2/explore/views`)
        ),
      createView: (view: CreateExploreView) =>
        apiResolver<ExploreView>(api.post(`/platform/v2/explore/views`, view)),
      updateView: (viewId: string, view: Partial<CreateExploreView>) =>
        apiResolver<ExploreView>(
          api.put(`/platform/v2/explore/views/${viewId}`, view)
        ),
      getViewBySlug: (viewSlug: string) =>
        apiResolver<ExploreView>(
          api.get(`/platform/v2/explore/views/${viewSlug}`)
        ),
      deleteView: (viewId: string) =>
        apiResolver<void>(api.delete(`/platform/v2/explore/views/${viewId}`)),
      subscribeToView: (
        viewId: string,
        data: { frequency: "weekly" | "monthly"; subscribersEmails: string[] }
      ) =>
        apiResolver<void>(
          api.post(`/platform/v2/explore/views/${viewId}/subscribe`, data)
        ),
      unsubscribeFromView: (viewId: string) =>
        apiResolver<void>(
          api.delete(`/platform/v2/explore/views/${viewId}/subscribe`)
        ),
    }),
    [api]
  );
};

export function useExploreInteractions({
  source,
  orderBy,
  orderDir,
  sorting,
  filter,
  enabled = false,
}: {
  source?: ExploreInteractionType;
  orderBy?: AllowedInteractionOrderBy | AllowedAggOrderBy;
  orderDir?: "asc" | "desc";
  sorting?: SortingState;
  filter?: Filter;
  enabled?: boolean;
}) {
  const { getInteractions } = useExploreApi();

  const requestParams = useMemo(() => {
    return buildParams({ source, orderBy, orderDir, sorting, filter });
  }, [source, orderBy, orderDir, sorting, filter]);

  const query = useInfiniteQuery({
    queryKey: ["explore-v2", source ?? "interaction", requestParams],
    queryFn: async ({ pageParam }) => {
      const params = (pageParam as string) ?? requestParams;
      const paramsString = params.length > 0 ? `?${params.toString()}` : "";

      return getInteractions(paramsString);
    },
    getNextPageParam: (lastPage) => lastPage?.next || undefined,
    refetchOnWindowFocus: false,
    keepPreviousData: true,
    enabled,
  });

  const flatData = useMemo(
    () => query.data?.pages?.flatMap((page) => page.results) ?? [],
    [query.data]
  );

  return {
    ...query,
    data: flatData,
  };
}

export function useExploreCount({
  source,
  filter,
}: {
  source?: ExploreInteractionType;
  filter?: Filter;
}) {
  const { getInteractionsCount } = useExploreApi();

  const requestParams = useMemo(() => {
    return buildParams({ source, filter });
  }, [source, filter]);

  const query = useQuery({
    queryKey: ["explore-v2", source ?? "interaction", "count", requestParams],
    queryFn: async () => {
      const paramsString =
        requestParams.length > 0 ? `?${requestParams.toString()}` : "";

      return getInteractionsCount(paramsString);
    },
    refetchOnWindowFocus: false,
  });

  return query;
}

export function stringifyDistinctValues(
  values: ExploreDistinctValues
): string[] {
  const result: string[] = [];

  values.forEach((value) => {
    if (typeof value === "string") {
      result.push(value);
    } else if (typeof value === "number") {
      result.push(value.toString());
    } else if (typeof value === "object" && value != null && "name" in value) {
      result.push(value.name);
    } else if (typeof value === "boolean") {
      result.push(value ? "true" : "false");
    }
  });

  return result;
}

export function useExploreValues({
  source,
}: {
  source?: ExploreInteractionType;
}) {
  const { getInteractionsValues } = useExploreApi();
  const requestParams = useMemo(() => {
    return buildParams({ source });
  }, [source]);

  const query = useQuery({
    queryKey: ["explore-v2", source ?? "interaction", "values", requestParams],
    queryFn: async () => {
      const paramsString =
        requestParams.length > 0 ? `?${requestParams.toString()}` : "";

      return getInteractionsValues(paramsString);
    },
    refetchOnWindowFocus: false,
    select: (data) => {
      return Object.fromEntries(
        Object.entries(data).map(([key, value]) => [
          key,
          stringifyDistinctValues(value),
        ])
      );
    },
  });

  return query;
}

export function useExploreAggInteractions({
  source,
  groupBy,
  orderBy,
  orderDir,
  filter,
  sorting,
  enabled = true,
}: {
  source?: ExploreInteractionType;
  groupBy?: string;
  orderBy?: AllowedInteractionOrderBy | AllowedAggOrderBy;
  orderDir?: "asc" | "desc";
  filter?: Filter;
  sorting?: SortingState;
  enabled?: boolean;
}) {
  const { getAggregatedInteractions } = useExploreApi();

  const requestParams = useMemo(() => {
    return buildParams({ source, groupBy, orderBy, orderDir, filter, sorting });
  }, [source, groupBy, orderBy, orderDir, filter, sorting]);

  const query = useQuery({
    queryKey: [
      "explore-v2",
      source ?? "interaction",
      "aggregated",
      requestParams,
    ],
    queryFn: async () => {
      const paramsString =
        requestParams.length > 0 ? `?${requestParams.toString()}` : "";

      return getAggregatedInteractions(paramsString);
    },
    enabled,
  });

  return query;
}

export function useExploreValueDistribution({
  source,
  groupBy,
  splitBy,
  metric,
  filter,
}: {
  source?: ExploreInteractionType;
  groupBy?: string;
  splitBy?: string;
  metric?: ExploreAggColumnType;
  filter?: Filter;
}) {
  const { getInteractionValueDistribution } = useExploreApi();

  const requestParams = useMemo(() => {
    const params = new URLSearchParams();

    if (source) {
      params.append("sources[]", source);
    }

    if (groupBy) {
      params.append("groupBy", groupBy);
    }

    if (filter) {
      const filterString = JSON.stringify(filter);
      params.append("filter", filterString);
    }

    if (splitBy) {
      params.append("column", splitBy);
    }

    if (metric) {
      params.append("metric", metric);
    }

    return params.toString();
  }, [groupBy, filter, splitBy, metric]);

  return useQuery({
    queryKey: [
      "explore-v2",
      source ?? "interaction",
      "value-distribution",
      requestParams,
    ],
    queryFn: async () => {
      const paramsString =
        requestParams.length > 0 ? `?${requestParams.toString()}` : "";

      return getInteractionValueDistribution(paramsString);
    },
    enabled: groupBy != null && splitBy != null,
  });
}
